import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
            
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-sm text-gray-500 mb-8">Last updated: January 2025</p>
              
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">What Are Cookies?</h2>
                <p>Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better browsing experience by remembering your preferences and analyzing how you use our site.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Cookies</h2>
                <p>We use cookies and similar technologies to collect and analyze information about users of this website. We use this information to enhance content, advertising, and other services on the site. Your personal data may also be used for ads personalization.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Types of Cookies We Use</h2>
                
                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Essential Cookies</h3>
                    <p className="text-gray-700">These cookies are necessary for the website to function properly. They enable basic functions like page navigation, access to secure areas, and shopping cart functionality.</p>
                    <p className="text-sm text-gray-600 mt-2"><strong>Duration:</strong> Session or up to 1 year</p>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Performance Cookies</h3>
                    <p className="text-gray-700">These cookies collect information about how visitors use our website, such as which pages are visited most often and if users get error messages.</p>
                    <p className="text-sm text-gray-600 mt-2"><strong>Duration:</strong> Up to 2 years</p>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Functionality Cookies</h3>
                    <p className="text-gray-700">These cookies allow the website to remember choices you make and provide enhanced, more personal features, such as language preferences and region selection.</p>
                    <p className="text-sm text-gray-600 mt-2"><strong>Duration:</strong> Up to 1 year</p>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Advertising Cookies</h3>
                    <p className="text-gray-700">These cookies are used to deliver advertisements that are more relevant to you and your interests. They may also be used to limit the number of times you see an advertisement.</p>
                    <p className="text-sm text-gray-600 mt-2"><strong>Duration:</strong> Up to 2 years</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h2>
                <p>We also use third-party services that may set cookies on your device:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                  <li><strong>Google Ads:</strong> For advertising and remarketing purposes</li>
                  <li><strong>Facebook Pixel:</strong> For social media advertising and analytics</li>
                  <li><strong>Payment Processors:</strong> For secure payment processing</li>
                </ul>
                <p className="mt-4">For more information on how Google uses your data, see <a href="https://business.safety.google/privacy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 underline">Google Business Safety & Privacy</a>.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
                <p>You have several options for managing cookies:</p>
                
                <div className="space-y-4 mt-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Cookie Consent Banner</h3>
                    <p className="text-blue-800">When you first visit our site, you'll see a cookie consent banner where you can choose to accept all cookies or customize your preferences.</p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-green-900 mb-2">Browser Settings</h3>
                    <p className="text-green-800">You can control cookies through your browser settings. Most browsers allow you to block or delete cookies, though this may affect website functionality.</p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-900 mb-2">Opt-Out Tools</h3>
                    <p className="text-yellow-800">You can opt out of interest-based advertising through industry opt-out tools like the Digital Advertising Alliance's opt-out page.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Browser-Specific Instructions</h2>
                <p>Here's how to manage cookies in popular browsers:</p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Chrome</h3>
                    <p className="text-sm text-gray-700">Settings → Privacy and security → Cookies and other site data</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Firefox</h3>
                    <p className="text-sm text-gray-700">Options → Privacy & Security → Cookies and Site Data</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Safari</h3>
                    <p className="text-sm text-gray-700">Preferences → Privacy → Manage Website Data</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Edge</h3>
                    <p className="text-sm text-gray-700">Settings → Cookies and site permissions → Cookies and site data</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Impact of Disabling Cookies</h2>
                <p>If you choose to disable cookies, some features of our website may not function properly:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Shopping cart functionality may be limited</li>
                  <li>User preferences and settings may not be saved</li>
                  <li>Some pages may load more slowly</li>
                  <li>Personalized content and recommendations may not work</li>
                  <li>Social media integration may be affected</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Updates to This Policy</h2>
                <p>We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                <p>If you have any questions about our use of cookies or this Cookie Policy, please contact us at:</p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p><strong>Email:</strong> privacy@gamestore.com</p>
                  <p><strong>Address:</strong> 123 Gaming Street, Digital City, DC 12345</p>
                  <p><strong>Phone:</strong> +1 (555) 123-4567</p>
                </div>
              </section>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-8">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">Your Consent</h3>
                <p className="text-purple-800">By clicking 'Accept all', you consent to the use of these technologies by our platform and its partners. You can adjust your consent by clicking 'Customize' in our cookie banner or by contacting us directly.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
